// ============================================================================
// pool.js — fixed-capacity object pool with O(1) spawn/despawn and a dense
// active list for zero-allocation iteration.
//
// All objects are constructed up front. Spawning hands back a reused object;
// despawning swap-removes it from the active list. Nothing here allocates
// after construction, which is the whole point — the game loop must not GC.
// ============================================================================

export class Pool {
  /**
   * @param {number} capacity   max simultaneously-live objects
   * @param {() => object} factory  creates a blank object (called `capacity` times)
   */
  constructor(capacity, factory) {
    this.capacity = capacity;
    this.items = new Array(capacity);       // all objects, indexed by slot id
    this.free = new Array(capacity);        // stack of free slot ids
    this.active = [];                       // dense list of live objects
    this.active.length = 0;
    for (let i = 0; i < capacity; i++) {
      const o = factory();
      o._slot = i;        // stable slot id (used as a persistent handle)
      o._active = false;
      o._ai = -1;         // index within `active` (for swap-remove)
      this.items[i] = o;
      this.free[i] = capacity - 1 - i;      // fill free stack
    }
    this.freeTop = capacity;                // number of free slots available
  }

  get count() { return this.active.length; }

  /** Spawn (or return null if exhausted). Caller initialises the object. */
  spawn() {
    if (this.freeTop === 0) return null;
    const slot = this.free[--this.freeTop];
    const o = this.items[slot];
    o._active = true;
    o._ai = this.active.length;
    this.active.push(o);
    return o;
  }

  /** Despawn a live object obtained from this pool. Safe to call once. */
  despawn(o) {
    if (!o._active) return;
    o._active = false;
    const ai = o._ai;
    const last = this.active.pop();
    if (last !== o) {
      this.active[ai] = last;
      last._ai = ai;
    }
    o._ai = -1;
    this.free[this.freeTop++] = o._slot;
  }

  /** Resolve a slot id back to its object (persistent handle lookup). */
  get(slot) { return this.items[slot]; }

  /** Despawn everything (level reset). */
  clear() {
    for (let i = this.active.length - 1; i >= 0; i--) {
      this.despawn(this.active[i]);
    }
  }
}
